import { clsx } from "clsx";
import { createRef, type ElementType, type FC, PureComponent, type ReactElement, useRef } from "react";
import { createPortal } from "react-dom";
import { type Layout, type Layouts, Responsive, WidthProvider } from "react-grid-layout";
import { AlertDialog, Tooltip, TooltipIntent, TriggerButton, useTooltip } from "widgets";

import type { Dictionary } from "@/app-utils/type.ts";
import { DashboardCardType, type IGenericCardConfiguration } from "@/redux/modules/dashboard.ts";

import type { IGenericCardProps } from "../core/GenericCard.js";
import { ControlledPopover } from "../core/overlays/components/OverlayControllers.js";
import SideBar from "../core/SideBar.js";
import AddDashboardCardPanel, { type IGenericDashboardCard } from "./AddDashboardCardPanel.js";
import GettingStartedDashboardCard from "./GettingStartedDashboardCard.js";
import { ProjectsDashboardCard2 } from "./ProjectsDashboardCard.js";
import { QuicklinkDashboardCard } from "./QuicklinkDashboardCard.js";
import RecentActivitiesDashboardCard from "./RecentActivitiesDashboardCard.js";
import { SampleStatusDashboardCard } from "./SampleStatusDashboardCard.js";
import SavedItemsDashboardCard from "./SavedItemsDashboardCard.js";

export interface IDashboardConfig {
  id: number;
  title: string;
  dashboardPermissions: any;
  sortOrder: number;
  cardsLayout: Layouts;
  cardsConfiguration: IGenericCardConfiguration[];
}

interface IDashboardViewProps {
  dashboard: IDashboardConfig;
  editMode: boolean;
  availableCards: IGenericDashboardCard[];

  toggleEditMode: () => void;
  saveDashboard: (dashboard: IDashboardConfig) => Promise<IDashboardConfig[]>;

  onFullScreen?: (isFullScreen: boolean) => void;
}

const ResponsiveGridLayout = WidthProvider(Responsive);
const cardTypeToComponentMap = {
  [DashboardCardType.projectsList]: ProjectsDashboardCard2,
  [DashboardCardType.quickLinks]: QuicklinkDashboardCard,
  [DashboardCardType.gettingStarted]: GettingStartedDashboardCard,
  [DashboardCardType.savedItems]: SavedItemsDashboardCard,
  [DashboardCardType.sampleStatuses]: SampleStatusDashboardCard,
  [DashboardCardType.recentActivities]: RecentActivitiesDashboardCard,
} as const satisfies Dictionary<ElementType>;

export interface IDashboardViewState {
  title: string;
  dashboardPermissions: any;
  cardsLayout: Layouts; // if we keep storing only the MD layout (3 columns) then we might convert this to simply Array<Layout>
  cardsConfiguration: IGenericCardConfiguration[];
  droppingCard: IGenericDashboardCard;

  fullscreenCard: string;
  showUndoPopover: boolean;
  showDeletePopover: boolean;
  showAddCardSidebar: boolean;
  unsavedChanges: boolean;
  canSave: boolean;
  isMovingCard: boolean;
}

class DashboardView extends PureComponent<IDashboardViewProps, IDashboardViewState> {
  private undoButtonRef = createRef<HTMLDivElement>();

  private static DROPPING_ITEM_ID = "droppingItemId";

  state = {
    title: this.props.dashboard.title,
    dashboardPermissions: this.props.dashboard.dashboardPermissions,
    cardsLayout: this.props.dashboard.cardsLayout,
    cardsConfiguration: this.props.dashboard.cardsConfiguration,
    fullscreenCard: null,
    showUndoPopover: false,
    showDeletePopover: false,
    showAddCardSidebar: false,
    unsavedChanges: false,
    droppingCard: null,
    isMovingCard: false,
    // As the 3 columns layout is the only default setup, if it starts with a 2 columns layout
    // "onBreakpointChange" will be called. So assuming canSave is true is correct in this case,
    // because "onBreakpointChange" won't be called if it's already a 3 column layout.
    canSave: true,
  };

  updateStateFromDashboardProps = () => {
    this.setState({
      title: this.props.dashboard.title,
      dashboardPermissions: this.props.dashboard.dashboardPermissions,
      cardsLayout: this.props.dashboard.cardsLayout,
      cardsConfiguration: this.props.dashboard.cardsConfiguration,
      unsavedChanges: false,
    });
  };

  componentDidUpdate(prevProps: Readonly<IDashboardViewProps>, prevState: IDashboardViewState): void {
    if (this.props.dashboard !== prevProps.dashboard) {
      this.updateStateFromDashboardProps();
    }
    if (this.state.cardsLayout !== prevState.cardsLayout) {
      this.setState({
        cardsConfiguration: DashboardView.sortCardsInConfiguration(
          this.state.cardsConfiguration,
          this.state.cardsLayout,
        ),
      });
    }
  }

  onLayoutChange = (_currentLayout: Layout[], allLayouts: Layouts) => {
    // hide the sidebar whenever we start changing the layout
    this.setState({ showAddCardSidebar: false });

    if (this.modifiedDashboardLayout(this.state.cardsLayout.md, allLayouts.md)) {
      this.setState({
        cardsLayout: { md: DashboardView.sortCardsInLayout(allLayouts.md) }, // only record the state of the "md" layout, the smaller layouts will collapse according to the order of the cards
        unsavedChanges: true,
      });
    }
  };

  onBreakpointChange(newCols: number) {
    const canSave = newCols === 3;

    if (canSave !== this.state.canSave) {
      this.setState({ canSave });
    }
  }

  modifiedDashboardLayout = (dashboardLayout1: Layout[], dashboardLayout2: Layout[]) => {
    return dashboardLayout1.find(
      (cardLayout1: Layout) =>
        !this.matchingCardLayout(
          cardLayout1,
          dashboardLayout2.find((cardLayout2: Layout) => cardLayout2.i === cardLayout1.i),
        ),
    ); // "moved" property is set by DragEvents and ResizeEvents
  };

  matchingCardLayout = (cardLayout1: Layout, cardLayout2: Layout) => {
    return (
      cardLayout1.w === cardLayout2.w &&
      cardLayout1.h === cardLayout2.h &&
      cardLayout1.x === cardLayout2.x &&
      cardLayout1.y === cardLayout2.y
    );
  };

  toggleFullscreenCard = (cardId: string) => {
    let isFullScreen = false;

    if (this.state.fullscreenCard === cardId) {
      this.setState({ fullscreenCard: null });
    } else {
      this.setState({ fullscreenCard: cardId });
      isFullScreen = true;
    }

    if (typeof this.props.onFullScreen === "function") {
      this.props.onFullScreen(isFullScreen);
    }
  };

  toggleAddCardSidebar = () => {
    if (this.state.canSave) {
      this.setState({ showAddCardSidebar: !this.state.showAddCardSidebar });
    }
  };

  removeCard = (cardId: string) => {
    this.setState({
      cardsConfiguration: this.state.cardsConfiguration.filter(
        (cardConfig: IGenericCardConfiguration) => cardConfig.id !== cardId,
      ),
      cardsLayout: { md: this.state.cardsLayout.md.filter((layout: Layout) => layout.i !== cardId) },
      unsavedChanges: true,
    });
  };

  saveCardConfig = (updatedCardConfiguration: IGenericCardConfiguration) => {
    this.setState({
      cardsConfiguration: this.state.cardsConfiguration.map((cardConfig: IGenericCardConfiguration) =>
        updatedCardConfiguration.id === cardConfig.id ? updatedCardConfiguration : cardConfig,
      ),
      unsavedChanges: true,
    });
  };

  saveDashboard = () => {
    if (!this.state.canSave) {
      return;
    }

    const dashboard: IDashboardConfig = {
      id: this.props.dashboard.id,
      title: this.state.title,
      sortOrder: this.props.dashboard.sortOrder,
      cardsConfiguration: DashboardView.sortCardsInConfiguration(this.state.cardsConfiguration, this.state.cardsLayout),
      cardsLayout: { md: DashboardView.sortCardsInLayout(this.state.cardsLayout.md) },
      dashboardPermissions: {},
    };
    this.props.saveDashboard(dashboard);
    this.props.toggleEditMode();
  };

  // see https://awesomeopensource.com/project/STRML/react-grid-layout#installation
  onCardDrop = (elemParams: Layout[], droppingItem: Layout) => {
    const cardId = `${this.state.droppingCard.defaultLayout.i}-${Date.now()}`;
    // get new layouts but set the final identifier of the newly dropped card
    const cards: Layout[] = elemParams.map((elem) => (elem.i === droppingItem.i ? { ...elem, i: cardId } : elem));
    const cardConfiguration: IGenericCardConfiguration = {
      id: cardId,
      title: this.state.droppingCard.name,
      type: this.state.droppingCard.type,
      specificConfig: this.state.droppingCard.specificConfig,
    };

    this.setState({
      unsavedChanges: true,
      cardsLayout: { ...this.state.cardsLayout, md: cards },
      cardsConfiguration: [cardConfiguration, ...this.state.cardsConfiguration], // the order is important, the new has to come first so that the drag'drop works fine
      droppingCard: null,
    });
  };

  // card configuration from the Cards Panel
  setDroppingItem = (card: IGenericDashboardCard) => {
    this.setState({ droppingCard: card });
  };

  static sortCardsInConfiguration = (cardsConfiguration: IGenericCardConfiguration[], cardLayout: Layouts) => {
    // [SEL6-2956] since we only provide the MD layout (3 columns layout) the other smaller layouts should collapse the cards accordingly,
    // which is done using the ordering of the card components, therefore we order them by x/y position in their layout ;
    // however this affects the dropping of new cards therefore we cannot do it on the fly in the render method
    return cardsConfiguration.slice(0).sort((a: IGenericCardConfiguration, b: IGenericCardConfiguration) => {
      const aLayout: Layout = cardLayout.md.find((layout: Layout) => layout.i === a.id);
      const bLayout: Layout = cardLayout.md.find((layout: Layout) => layout.i === b.id);
      return aLayout.y === bLayout.y ? (aLayout.x > bLayout.x ? 1 : -1) : aLayout.y > bLayout.y ? 1 : -1;
    });
  };

  static sortCardsInLayout = (cardLayout: Layout[]) => {
    // [SEL6-2956] since we only provide the MD layout (3 columns layout) the other smaller layouts should collapse the cards accordingly,
    // which is done using the ordering of the card components, therefore we order them by x/y position in their layout ;
    // however this affects the dropping of new cards therefore we have to do it when onBreakpointChange is triggered
    return cardLayout.slice(0).sort((aLayout: Layout, bLayout: Layout) => {
      return aLayout.y === bLayout.y ? (aLayout.x > bLayout.x ? 1 : -1) : aLayout.y > bLayout.y ? 1 : -1;
    });
  };

  render(): ReactElement {
    const isDraggable: boolean = this.props.editMode && this.state.canSave;
    const droppingItem = this.state.droppingCard && {
      ...this.state.droppingCard.defaultLayout,
      i: DashboardView.DROPPING_ITEM_ID,
    };
    const editToolbar = this.props.editMode
      ? createPortal(
          <div className={"dashboard-edit-mode-overlay"}>
            <div className={"dashboard-edit-mode-toolbar"} onClick={this.preventClickOnOverlay}>
              <div className={"dashboard-edit-mode-title"}>{this.state.title} </div>
              <div className={"dashboard-edit-mode-actions"}>
                <TriggerButton
                  icon={"library_add"}
                  label={"Add Card"}
                  showLabel
                  tooltip={"Add and configure an informative card to your dashboard"}
                  isDisabled={!this.state.canSave}
                  onPress={this.toggleAddCardSidebar}
                />
                <SaveDashboardButton
                  canSave={this.state.canSave}
                  saveCallback={this.saveDashboard}
                  unsavedChanges={this.state.unsavedChanges}
                />
                <TriggerButton
                  icon={"close"}
                  label={"Cancel"}
                  tooltip={"You will not be able to recover your unsaved changes"}
                  showLabel
                  onPress={this.handleUndoAction}
                  ref={this.undoButtonRef}
                />
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

    return (
      <>
        {this.props.editMode
          ? createPortal(
              <SideBar open={this.state.showAddCardSidebar} position={"right"}>
                <AddDashboardCardPanel
                  cards={this.props.availableCards}
                  onClose={this.toggleAddCardSidebar}
                  setDroppingItem={this.setDroppingItem}
                />
              </SideBar>,
              document.body,
            )
          : null}

        <ResponsiveGridLayout
          className={clsx("dashboard", {
            fullscreenCard: this.state.fullscreenCard,
            "dragging-card": !!this.state.droppingCard,
          })}
          layouts={{
            md: this.state.cardsLayout.md.map((layout) => ({
              ...layout,
              isDraggable: isDraggable,
            })),
          }} // only provide the MD layout and let the other layouts behave according to the cards components order
          breakpoints={{ md: 1160, sm: 784, xs: 0 }} // app min size is 784px
          cols={{ md: 3, sm: 2, xs: 1 }}
          containerPadding={[7, 7]}
          margin={[7, 7]}
          rowHeight={40}
          onLayoutChange={this.onLayoutChange}
          onBreakpointChange={(_, newCols) => this.onBreakpointChange(newCols)}
          isDraggable={isDraggable}
          isResizable={isDraggable}
          isDroppable={true}
          onDrop={this.onCardDrop}
          droppingItem={droppingItem}
          onDragStart={() => this.setState({ isMovingCard: true })}
          onDragStop={() => this.setState({ isMovingCard: false })}
          useCSSTransforms={false}
        >
          {this.state.cardsConfiguration.map((cardConfig: IGenericCardConfiguration) => {
            const CardComponent: ElementType<IGenericCardProps> = cardTypeToComponentMap[cardConfig.type];
            const isVisible: boolean = !this.state.fullscreenCard || this.state.fullscreenCard === cardConfig.id;
            return (
              <div
                key={cardConfig.id}
                className={clsx({
                  "edit-mode": this.props.editMode,
                  "display-mode": !this.props.editMode,
                  fullscreen: this.state.fullscreenCard === cardConfig.id,
                })}
                style={{ opacity: isVisible ? 1 : 0, pointerEvents: isVisible ? "auto" : "none" }}
              >
                {CardComponent && (
                  <CardComponent
                    id={cardConfig.id}
                    editMode={this.props.editMode}
                    title={cardConfig.title}
                    toggleFullscreen={this.toggleFullscreenCard}
                    isFullscreen={this.state.fullscreenCard === cardConfig.id}
                    removeCard={this.removeCard}
                    config={cardConfig}
                    saveCardConfig={this.saveCardConfig}
                    isDraggable={this.state.canSave}
                    isMovingCard={this.state.isMovingCard}
                  />
                )}
              </div>
            );
          })}
        </ResponsiveGridLayout>
        {editToolbar}

        {this.state.showUndoPopover && (
          <ControlledPopover triggerRef={this.undoButtonRef} close={() => this.setState({ showUndoPopover: false })}>
            {(controllerProps) => (
              <AlertDialog
                {...controllerProps}
                mainButton={{
                  label: "Reset and Close",
                  onPress: this.undoModifications,
                }}
              >
                Reset the position of the cards to the previous saved state?
              </AlertDialog>
            )}
          </ControlledPopover>
        )}
      </>
    );
  }

  preventClickOnOverlay = (event) => {
    event.stopPropagation();
  };

  handleUndoAction = () => {
    if (this.state.unsavedChanges) {
      this.setState({ showUndoPopover: true });
    } else {
      this.undoModifications();
    }
  };

  undoModifications = () => {
    this.updateStateFromDashboardProps();
    this.setState({ showUndoPopover: false });
    this.props.toggleEditMode();
  };
}

interface SaveDashboardButtonProps {
  canSave: boolean;
  saveCallback: () => void;
  unsavedChanges: boolean;
}
const SaveDashboardButton: FC<SaveDashboardButtonProps> = (props) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { state, tooltipProps, triggerProps, arrowProps } = useTooltip(triggerRef, tooltipRef, {
    isOpen: !props.canSave,
  });

  return (
    <>
      <TriggerButton
        {...triggerProps}
        icon={"cloud_upload"}
        label={"Save Dashboard"}
        showLabel
        isDisabled={!props.canSave}
        onPress={() => {
          props.saveCallback();
        }}
        badge={props.unsavedChanges}
        ref={triggerRef}
      />
      {state.isOpen && (
        <Tooltip
          intent={TooltipIntent.Warning}
          title={"Save Dashboard"}
          content={
            "The width of your browser window will affect how cards are displayed on the screen. To save this layout you must increase the width of your browser window."
          }
          domProps={tooltipProps}
          arrowProps={arrowProps}
          ref={tooltipRef}
        />
      )}
    </>
  );
};

export default DashboardView;
